/**
 * ProcedureFormModals.tsx — the three forms of Admin > Procedures, with
 * legacy's fields (admin_procedures_list.php's edit panels):
 *
 *   - Category: Title *, Description, Hint.
 *   - Sub-Category: Category *, Title *, Hint, and its Documents — which
 *     document fills packet slot N when a packet item is "procedure slot N".
 *     Legacy's "General Inquiry" is left out (user, 2026-10-14): legacy's save
 *     ignored it (admin_procedures_save.php comments the read out), and
 *     procedures_subcategories.inquiryID is imported I&E data that the I&E PDF
 *     builder groups inquiry sections by, not the plan reason the dropdown lists.
 *   - Procedure: Sub-Category *, Title *, Description, Diagnosis, Service /
 *     Anesthesia / Operating Room Fee, Hours, HCPC, and, for accounts with I&E
 *     content, Set as / Same as Standard. HCPC works as legacy's (user,
 *     2026-10-14): a read-only box that opens the HCPCS Directory when clicked.
 *
 * Changing the parent moves the row to the end of the new one. Each form
 * saves on its own (legacy saved the whole tree at once).
 *
 * On a Locked page AdminFormModal makes each a viewer; the sub-category's
 * document rows also lose Add / Remove and the HCPC box no longer opens the
 * directory. The I&E content buttons stay: that editor is read-only there too.
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Plus, Search, X } from 'lucide-react';
import { AdminFormModal } from '@/components/admin/AdminFormModal';
import { ADMIN_TD_CLASS, ADMIN_TH_CLASS, PaginationFooter } from '@/components/admin/AdminList';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { DialogField, fieldClasses } from '@/components/admin/DialogChrome';
import type { ListPaginationState } from '@/components/admin/useListPagination';
import { useTrackedForm } from '@/components/admin/useTrackedForm';
import { Button } from '@/components/ui/Button';
import { Checkbox } from '@/components/ui/Checkbox';
import { useFieldLocked } from '@/components/ui/FieldLock';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { cn } from '@/lib/utils';
import { SearchableCombobox, type ComboboxOption } from '@/components/ui/SearchableCombobox';
import { Select } from '@/components/ui/Select';
import { Textarea } from '@/components/ui/Textarea';
import { getErrorMessage } from '@/services/apiClient';
import {
  DOCUMENT_SLOT_MAX,
  HCPC_PAGE_SIZE,
  PROCEDURE_NAME_MAX,
  createProcedureRow,
  hourOptions,
  hoursValue,
  searchHcpcCodes,
  updateProcedureRow,
  type AdminProcedure,
  type AdminProcedureCategory,
  type AdminProcedureSubcategory,
  type HcpcPage,
  type SubcategoryDocument,
} from '@/services/proceduresAdmin.service';
import { toast } from '@/store/toastStore';

type Category = Omit<AdminProcedureCategory, 'subcategories'>;
type Subcategory = Omit<AdminProcedureSubcategory, 'procedures'>;

/** The gap between fields, as the other Admin forms (Products): a label never sits on the field above. */
const FIELD_STACK = 'space-y-4';

function nameField(name: string, onChange: (v: string) => void, id: string, badge: ReactNode, placeholder: string) {
  const missing = name.trim() === '';
  return (
    <DialogField
      label="Title"
      htmlFor={id}
      required
      state={missing ? 'error' : 'none'}
      labelHint={missing ? '— required' : undefined}
      badge={badge}
    >
      <Input
        id={id}
        value={name}
        maxLength={PROCEDURE_NAME_MAX}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={fieldClasses.input(missing ? 'error' : 'none')}
      />
    </DialogField>
  );
}

// ─── Category ─────────────────────────────────────────────────────────────

export function ProcedureCategoryModal({
  category,
  onClose,
  onSaved,
}: {
  category: Category | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = category === null;
  const t = useTrackedForm(
    () => ({ name: category?.name ?? '', description: category?.description ?? '', hint: category?.hint ?? '' }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const missing = t.form.name.trim() === '';

  async function save() {
    if (missing) return;
    setSaving(true);
    try {
      if (category) await updateProcedureRow('categories', category.id, t.form);
      else await createProcedureRow('categories', t.form);
      toast.success(isNew ? 'Category created' : 'Category updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The category could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Category' : 'Edit Category'}
      isNew={isNew}
      dirty={isNew ? !missing : t.dirty}
      saving={saving}
      errors={missing ? ['Title is required'] : []}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      size="md"
    >
      <div className={FIELD_STACK}>
        {nameField(t.form.name, (name) => t.patch({ name }), 'procedure-category-name', t.badge('name', 'Title'), 'e.g. Face')}
        <DialogField label="Description" htmlFor="procedure-category-description" badge={t.badge('description', 'Description')}>
          <Textarea autoResize
            id="procedure-category-description"
            rows={3}
            value={t.form.description}
            onChange={(e) => t.patch({ description: e.target.value })}
          />
        </DialogField>
        <DialogField label="Hint" htmlFor="procedure-category-hint" badge={t.badge('hint', 'Hint')}>
          <Textarea id="procedure-category-hint" rows={2} value={t.form.hint} onChange={(e) => t.patch({ hint: e.target.value })} />
        </DialogField>
      </div>
    </AdminFormModal>
  );
}

// ─── Sub-category ─────────────────────────────────────────────────────────

export interface DocumentChoice {
  id: number;
  title: string;
  category: string;
}

const SLOT_OPTIONS = Array.from({ length: DOCUMENT_SLOT_MAX }, (_, i) => ({ value: i + 1, label: String(i + 1) }));

export function ProcedureSubcategoryModal({
  subcategory,
  defaultCategoryId,
  categories,
  documents,
  documentsLoading,
  hasIE,
  onEditInquiry,
  onClose,
  onSaved,
}: {
  subcategory: AdminProcedureSubcategory | null;
  defaultCategoryId: number;
  /** Live categories (the current one too, even when deleted). */
  categories: Category[];
  /** The account's live documents. */
  documents: DocumentChoice[];
  documentsLoading: boolean;
  hasIE: boolean;
  onEditInquiry: (sub: Subcategory) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = subcategory === null;
  const locked = useAdminPageLevel() === 'locked';
  const t = useTrackedForm(
    () => ({
      categoryId: subcategory?.categoryId ?? defaultCategoryId,
      name: subcategory?.name ?? '',
      hint: subcategory?.hint ?? '',
      documents: (subcategory?.documents ?? []) as SubcategoryDocument[],
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const missing = t.form.name.trim() === '';
  const noCategory = !t.form.categoryId;
  const docRowsIncomplete = t.form.documents.some((d) => !d.documentId);

  const categoryOptions = categories.map((c) => ({ value: c.id, label: c.name.trim() || `Category #${c.id}` }));
  const docOptions: ComboboxOption<number>[] = useMemo(() => {
    const known = new Set(documents.map((d) => d.id));
    const opts = documents.map((d) => ({ value: d.id, label: d.title.trim() || `Document #${d.id}`, group: d.category || 'No category' }));
    // A linked document that is no longer live still shows what it is.
    for (const row of t.form.documents) {
      if (row.documentId && !known.has(row.documentId)) {
        opts.push({ value: row.documentId, label: `Document #${row.documentId} (deleted)`, group: 'Deleted' });
        known.add(row.documentId);
      }
    }
    return opts;
  }, [documents, t.form.documents]);

  const errors = [
    ...(missing ? ['Title is required'] : []),
    ...(noCategory ? ['Category is required'] : []),
    ...(docRowsIncomplete ? ['Choose a document for every document row, or remove the row'] : []),
  ];

  function setDoc(i: number, patch: Partial<SubcategoryDocument>) {
    t.patch({ documents: t.form.documents.map((d, j) => (j === i ? { ...d, ...patch } : d)) });
  }

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    try {
      if (subcategory) await updateProcedureRow('subcategories', subcategory.id, t.form);
      else await createProcedureRow('subcategories', t.form);
      toast.success(isNew ? 'Sub-category created' : 'Sub-category updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The sub-category could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <AdminFormModal
      title={isNew ? 'Add Sub-Category' : 'Edit Sub-Category'}
      description="Plans, leads and tasks call a sub-category the procedure."
      isNew={isNew}
      dirty={isNew ? !missing : t.dirty}
      saving={saving}
      errors={errors}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      footerExtra={
        hasIE && subcategory ? (
          <Button variant="secondary" size="sm" onClick={() => onEditInquiry(subcategory)}>
            Inquiry content
          </Button>
        ) : undefined
      }
    >
      <div className={FIELD_STACK}>
        <DialogField
          label="Category"
          htmlFor="procedure-subcategory-category"
          required
          badge={t.badge('categoryId', 'Category')}
          description={!isNew && t.form.categoryId !== subcategory?.categoryId ? 'It moves to the end of this category.' : undefined}
        >
          <Select<number>
            id="procedure-subcategory-category"
            options={categoryOptions}
            value={t.form.categoryId || null}
            onChange={(v) => t.patch({ categoryId: v ?? 0 })}
            searchable={categoryOptions.length > 8}
          />
        </DialogField>
        {nameField(t.form.name, (name) => t.patch({ name }), 'procedure-subcategory-name', t.badge('name', 'Title'), 'e.g. Rhinoplasty')}
        <DialogField label="Hint" htmlFor="procedure-subcategory-hint" badge={t.badge('hint', 'Hint')}>
          <Textarea id="procedure-subcategory-hint" rows={2} value={t.form.hint} onChange={(e) => t.patch({ hint: e.target.value })} />
        </DialogField>

        <DialogField
          label="Documents"
          badge={t.badge('documents', 'Documents')}
          description="When a document packet has a procedure slot, the document set for that slot here goes in its place."
        >
          <div className="space-y-2" data-testid="subcategory-documents">
            {t.form.documents.map((d, i) => (
              <div key={d.id || `new-${i}`} className="flex items-start gap-2">
                <div className="w-24 shrink-0">
                  <label htmlFor={`subcategory-document-slot-${i}`} className="sr-only">
                    {`Slot for document row ${i + 1}`}
                  </label>
                  <Select<number>
                    id={`subcategory-document-slot-${i}`}
                    options={SLOT_OPTIONS}
                    value={d.slot}
                    onChange={(v) => setDoc(i, { slot: v ?? 1 })}
                    searchable
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <SearchableCombobox<number>
                    id={`subcategory-document-${i}`}
                    placeholder="Choose a document…"
                    options={docOptions}
                    value={d.documentId || null}
                    onChange={(v) => setDoc(i, { documentId: v ?? 0 })}
                    loading={documentsLoading}
                    emptyMessage="No documents found"
                  />
                </div>
                {!locked && (
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Remove document row ${i + 1}`}
                    leftIcon={<X className="h-4 w-4" />}
                    onClick={() => t.patch({ documents: t.form.documents.filter((_, j) => j !== i) })}
                  />
                )}
              </div>
            ))}
            {!locked && (
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Plus className="h-4 w-4" />}
                onClick={() => t.patch({ documents: [...t.form.documents, { id: 0, slot: 1, documentId: 0 }] })}
              >
                Add document
              </Button>
            )}
          </div>
        </DialogField>
      </div>
    </AdminFormModal>
  );
}

// ─── Procedure ────────────────────────────────────────────────────────────

const MONEY = /^\d{0,8}(\.\d{0,2})?$/;

export function ProcedureModal({
  procedure,
  defaultSubcategoryId,
  subcategories,
  maxHours,
  hasIE,
  onEditContent,
  onClose,
  onSaved,
}: {
  procedure: AdminProcedure | null;
  defaultSubcategoryId: number;
  /** Live sub-categories as "Category › Sub-Category" (the current one too). */
  subcategories: { id: number; label: string }[];
  maxHours: number;
  hasIE: boolean;
  onEditContent: (procedure: AdminProcedure) => void;
  onClose: () => void;
  onSaved: () => void;
}) {
  const isNew = procedure === null;
  const t = useTrackedForm(
    () => ({
      subcategoryId: procedure?.subcategoryId ?? defaultSubcategoryId,
      name: procedure?.name ?? '',
      laymansTerms: procedure?.laymansTerms ?? '',
      diagnosis: procedure?.diagnosis ?? '',
      feeToPatient: procedure?.feeToPatient ?? '0.00',
      anesthesiaFee: procedure?.anesthesiaFee ?? '0.00',
      operatingRoomFee: procedure?.operatingRoomFee ?? '0.00',
      expNumHours: hoursValue(procedure?.expNumHours ?? '0'),
      hcpcCode: procedure?.hcpcCode ?? '',
      sameAsStd: procedure?.sameAsStd ?? false,
      setAsStd: procedure?.setAsStd ?? false,
    }),
    { tracking: !isNew },
  );
  const [saving, setSaving] = useState(false);
  const missing = t.form.name.trim() === '';
  const noSub = !t.form.subcategoryId;
  const badFees = (['feeToPatient', 'anesthesiaFee', 'operatingRoomFee'] as const).filter((k) => !MONEY.test(t.form[k].trim()));
  const hours = useMemo(() => {
    const opts = hourOptions(maxHours);
    // A stored value past the list still shows (legacy blanked it).
    if (!opts.some((o) => o.value === t.form.expNumHours)) opts.push({ value: t.form.expNumHours, label: t.form.expNumHours });
    return opts;
  }, [maxHours, t.form.expNumHours]);

  const errors = [
    ...(missing ? ['Title is required'] : []),
    ...(noSub ? ['Sub-Category is required'] : []),
    ...(badFees.length > 0 ? ['Fees must be amounts such as 1250 or 1250.00'] : []),
  ];

  async function save() {
    if (errors.length > 0) return;
    setSaving(true);
    const payload = {
      ...t.form,
      feeToPatient: t.form.feeToPatient.trim() || '0',
      anesthesiaFee: t.form.anesthesiaFee.trim() || '0',
      operatingRoomFee: t.form.operatingRoomFee.trim() || '0',
    };
    try {
      if (procedure) await updateProcedureRow('procedures', procedure.id, payload);
      else await createProcedureRow('procedures', payload);
      toast.success(isNew ? 'Procedure created' : 'Procedure updated');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'The procedure could not be saved.'));
    } finally {
      setSaving(false);
    }
  }

  const fee = (key: 'feeToPatient' | 'anesthesiaFee' | 'operatingRoomFee', label: string) => {
    const bad = badFees.includes(key);
    return (
      <DialogField label={label} htmlFor={`procedure-${key}`} state={bad ? 'error' : 'none'} badge={t.badge(key, label)}>
        <Input
          id={`procedure-${key}`}
          inputMode="decimal"
          leftAddon="$"
          value={t.form[key]}
          onChange={(e) => t.patch({ [key]: e.target.value.replace(/[$,%\s]/g, '') })}
          className={fieldClasses.input(bad ? 'error' : 'none')}
        />
      </DialogField>
    );
  };

  return (
    <AdminFormModal
      title={isNew ? 'Add Procedure' : 'Edit Procedure'}
      isNew={isNew}
      dirty={isNew ? !missing : t.dirty}
      saving={saving}
      errors={errors}
      onSave={() => void save()}
      onRevertAll={t.revertAll}
      onClose={onClose}
      footerExtra={
        hasIE && procedure ? (
          <Button variant="secondary" size="sm" onClick={() => onEditContent(procedure)}>
            Document content
          </Button>
        ) : undefined
      }
    >
      <div className={FIELD_STACK}>
        <DialogField
          label="Sub-Category"
          htmlFor="procedure-subcategory"
          required
          badge={t.badge('subcategoryId', 'Sub-Category')}
          description={!isNew && t.form.subcategoryId !== procedure?.subcategoryId ? 'It moves to the end of this sub-category.' : undefined}
        >
          <Select<number>
            id="procedure-subcategory"
            options={subcategories.map((s) => ({ value: s.id, label: s.label }))}
            value={t.form.subcategoryId || null}
            onChange={(v) => t.patch({ subcategoryId: v ?? 0 })}
            searchable={subcategories.length > 8}
          />
        </DialogField>
        {nameField(t.form.name, (name) => t.patch({ name }), 'procedure-name', t.badge('name', 'Title'), 'e.g. Open rhinoplasty')}
        <DialogField label="Description" htmlFor="procedure-description" badge={t.badge('laymansTerms', 'Description')}>
          <Textarea autoResize id="procedure-description" rows={3} value={t.form.laymansTerms} onChange={(e) => t.patch({ laymansTerms: e.target.value })} />
        </DialogField>
        <DialogField
          label="Diagnosis"
          htmlFor="procedure-diagnosis"
          badge={t.badge('diagnosis', 'Diagnosis')}
          description="Documents show this where they use the procedure's diagnosis."
        >
          <Textarea id="procedure-diagnosis" rows={2} value={t.form.diagnosis} onChange={(e) => t.patch({ diagnosis: e.target.value })} />
        </DialogField>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {fee('feeToPatient', 'Service Fee')}
          {fee('anesthesiaFee', 'Anesthesia Fee')}
          {fee('operatingRoomFee', 'Operating Room Fee')}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <DialogField label="Hours" htmlFor="procedure-hours" badge={t.badge('expNumHours', 'Hours')}>
            <Select<string>
              id="procedure-hours"
              options={hours}
              value={t.form.expNumHours}
              onChange={(v) => t.patch({ expNumHours: v ?? '0' })}
              searchable
            />
          </DialogField>
          <DialogField label="HCPC" htmlFor="procedure-hcpc" badge={t.badge('hcpcCode', 'HCPC')}>
            <HcpcField id="procedure-hcpc" value={t.form.hcpcCode} onChange={(hcpcCode) => t.patch({ hcpcCode })} />
          </DialogField>
        </div>
        {hasIE && (
          <div className="flex flex-wrap gap-6">
            <Checkbox
              label="Set as Standard"
              checked={t.form.sameAsStd}
              onChange={(e) => t.patch({ sameAsStd: e.target.checked })}
            />
            <Checkbox
              label="Same as Standard"
              checked={t.form.setAsStd}
              onChange={(e) => t.patch({ setAsStd: e.target.checked })}
            />
          </div>
        )}
      </div>
    </AdminFormModal>
  );
}

/**
 * Legacy's HCPC field (admin_procedures_list.php): a read-only box holding the
 * code. Clicking it opens the HCPCS Directory; picking a code fills the box.
 * It cannot be typed in or emptied, as legacy. On a Locked page it only shows
 * the code.
 */
function HcpcField({ id, value, onChange }: { id: string; value: string; onChange: (code: string) => void }) {
  const locked = useFieldLocked();
  const [open, setOpen] = useState(false);

  return (
    <>
      <Input
        id={id}
        value={value}
        readOnly
        placeholder={locked ? undefined : 'Click to choose a code'}
        aria-haspopup={locked ? undefined : 'dialog'}
        className={locked ? undefined : 'cursor-pointer'}
        onClick={locked ? undefined : () => setOpen(true)}
        onKeyDown={
          locked
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpen(true);
                }
              }
        }
      />
      {open && (
        <HcpcDirectoryModal
          onClose={() => setOpen(false)}
          onPick={(code) => {
            onChange(code);
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

/**
 * Legacy's "HCPCS Directory" pop-up (admin_hcpc_list.php): the system-wide
 * code list, every code before a search, 50 a page, with legacy's columns
 * Code, Summary and Description. A search matches every word against the code
 * or the summary. Clicking a row (any of its cells, as legacy) picks the code.
 */
function HcpcDirectoryModal({ onClose, onPick }: { onClose: () => void; onPick: (code: string) => void }) {
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [result, setResult] = useState<HcpcPage | null>(null);
  const [failed, setFailed] = useState(false);

  // The search waits for typing to pause, then starts again from page 1.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      setQuery(search.trim());
      setPage(1);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setResult(null);
    setFailed(false);
    searchHcpcCodes(query, page)
      .then((r) => {
        if (!cancelled) setResult(r);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [query, page]);

  const total = result?.total ?? 0;
  const pagination: ListPaginationState = {
    paged: true,
    page,
    pageSize: HCPC_PAGE_SIZE,
    initialPageSize: HCPC_PAGE_SIZE,
    total,
    totalPages: Math.max(1, Math.ceil(total / HCPC_PAGE_SIZE)),
    from: total === 0 ? 0 : (page - 1) * HCPC_PAGE_SIZE + 1,
    to: Math.min(page * HCPC_PAGE_SIZE, total),
    setPage,
    setPageSize: () => undefined,
  };

  const message = (text: string) => (
    <tr>
      <td colSpan={3} className={cn(ADMIN_TD_CLASS, 'text-slate-500')}>
        {text}
      </td>
    </tr>
  );

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="HCPCS Directory"
      size="xl"
      panelClassName="md:max-w-[990px]"
      pinned={
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" aria-hidden />
          <Input
            aria-label="Search the HCPCS directory"
            placeholder="Search by code or summary…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            autoFocus
          />
        </div>
      }
    >
      <div className="rounded-t-lg border border-slate-200" data-testid="hcpc-directory">
        <table className="w-full table-fixed text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              <th className={cn(ADMIN_TH_CLASS, 'w-24 text-left')}>Code</th>
              <th className={cn(ADMIN_TH_CLASS, 'w-[30%] text-left')}>Summary</th>
              <th className={cn(ADMIN_TH_CLASS, 'text-left')}>Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {failed
              ? message('The directory could not be loaded. Close it and try again.')
              : result === null
                ? message('Loading codes…')
                : result.codes.length === 0
                  ? message('No codes found')
                  : result.codes.map((c) => (
                      <tr key={c.code} className="cursor-pointer hover:bg-slate-50" onClick={() => onPick(c.code)} data-testid="hcpc-row">
                        <td className={ADMIN_TD_CLASS}>
                          <button
                            type="button"
                            className="font-mono font-medium text-blue-600 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={(e) => {
                              e.stopPropagation();
                              onPick(c.code);
                            }}
                          >
                            {c.code}
                          </button>
                        </td>
                        <td className={cn(ADMIN_TD_CLASS, 'truncate')} title={c.summary || undefined}>
                          {c.summary}
                        </td>
                        <td className={cn(ADMIN_TD_CLASS, 'text-slate-600')}>{c.description}</td>
                      </tr>
                    ))}
          </tbody>
        </table>
      </div>
      <PaginationFooter pagination={pagination} noun={['code', 'codes']} pageSizeOptions={[HCPC_PAGE_SIZE]} />
    </Modal>
  );
}
